"use client";

import { cn } from "@borderless/ui/lib/utils";
import type { Map as LeafletMap } from "leaflet";
import "leaflet/dist/leaflet.css";
import { useEffect, useRef } from "react";

interface MapDisplayProps {
  latitude: number;
  longitude: number;
  zoom?: number;
  className?: string;
}

export function MapDisplay({
  latitude,
  longitude,
  zoom = 13,
  className,
}: MapDisplayProps) {
  const mapRef = useRef<LeafletMap | null>(null);
  const mapContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!mapContainerRef.current) return;

    // Dynamically import Leaflet only on client side
    void import("leaflet").then((L) => {
      const container = mapContainerRef.current;
      if (!container) return;

      // Initialize the map if it hasn't been initialized yet
      if (!mapRef.current) {
        mapRef.current = L.map(container).setView([latitude, longitude], zoom);
      } else {
        mapRef.current.setView([latitude, longitude], zoom);
      }

      // Add CartoDB Positron tiles for a clean, simplified look
      L.tileLayer(
        "https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png",
        {
          maxZoom: 19,
          attribution:
            '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>',
        },
      ).addTo(mapRef.current);

      // Add marker with custom icon
      const markerIcon = L.divIcon({
        className: "custom-marker",
        html: `<svg width="24" height="36" viewBox="0 0 24 36" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 0C5.37258 0 0 5.37258 0 12C0 18.6274 12 36 12 36C12 36 24 18.6274 24 12C24 5.37258 18.6274 0 12 0ZM12 16C10.3431 16 9 14.6569 9 13C9 11.3431 10.3431 10 12 10C13.6569 10 15 11.3431 15 13C15 14.6569 13.6569 16 12 16Z" fill="#3B82F6"/>
        </svg>`,
        iconSize: [24, 36],
        iconAnchor: [12, 36],
      });

      L.marker([latitude, longitude], { icon: markerIcon }).addTo(
        mapRef.current,
      );
    });

    // Cleanup function
    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
      }
    };
  }, [latitude, longitude, zoom]);

  return (
    <div ref={mapContainerRef} className={cn("z-0 h-full w-full", className)} />
  );
}
